import React, { useCallback, useEffect } from "react";
import { useBlocker, Outlet } from "react-router-dom";
import { observer } from "mobx-react-lite";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { useRootStore } from "../store/contexts/RootStoreContext";

/**
 * NavigationGuard - Componente centralizado para bloqueo de navegación
 *
 * Arquitectura:
 * - Se ubica en RouterSync, dentro del contexto del Router
 * - Usa useBlocker de React Router para interceptar navegación interna
 * - Verifica hasPendingChanges en todos los stores del registry
 * - Muestra diálogo consistente con opciones claras
 *
 * Cobertura:
 * - Navegación interna (menú, breadcrumbs, links)
 * - Browser back/forward
 * - useBeforeUnload cubre abandono de página (definido aparte)
 */
export const NavigationGuard = observer(() => {
  const rootStore = useRootStore();
  const { langStore } = rootStore;

  const hasUnsavedChanges = useCallback(() => {
    const storesWithChanges = rootStore.storeRegistry.allStores.filter(
      (store) => "hasPendingChanges" in store && (store as any).hasPendingChanges
    );
    return storesWithChanges.length > 0;
  }, [rootStore.storeRegistry]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    // No bloquear si es la misma ruta
    if (currentLocation.pathname === nextLocation.pathname) {
      return false;
    }

    // No bloquear si es navegación de "Add new" (tiene callbackParams en el state)
    const nextState = nextLocation.state as { callbackParams?: any } | null;
    if (nextState?.callbackParams && !nextState.callbackParams.returnData) {
      return false;
    }

    // No bloquear si es retorno de "Add new" (tiene callbackParams con returnData)
    if (nextState?.callbackParams?.returnData) {
      return false;
    }

    // No bloquear si algún store está esperando callback
    const storeWaitingCallback = rootStore.storeRegistry.allStores.some(
      (store) => store.isWaitingForCallback
    );
    if (storeWaitingCallback) {
      return false;
    }

    // Bloquear si hay cambios sin guardar
    return hasUnsavedChanges();
  });

  // Manejar beforeunload para abandono de página
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        const message = langStore.UnsavedChangesWarning ||
          "You have unsaved changes. Are you sure you want to leave?";
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges, langStore]);

  const handleDiscard = () => {
    if (blocker.state === "blocked") {
      blocker.proceed();
    }
  };

  const handleCancel = () => {
    if (blocker.state === "blocked") {
      blocker.reset();
    }
  };

  return (
    <>
      <Outlet />

      <Dialog
        open={blocker.state === "blocked"}
        onClose={handleCancel}
        aria-labelledby="navigation-guard-dialog-title"
        aria-describedby="navigation-guard-dialog-description"
      >
        <DialogTitle id="navigation-guard-dialog-title">
          {langStore.unsavedChanges || "Cambios sin guardar"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="navigation-guard-dialog-description">
            {langStore.unsavedChangesMessage ||
              "Hay cambios sin guardar. ¿Descartarlos y continuar?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="primary">
            {langStore.cancel || "Cancelar"}
          </Button>
          <Button onClick={handleDiscard} color="error" variant="contained">
            {langStore.discardChanges || "Descartar cambios"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});
